import React, { useState } from 'react';
import { Button, Dropdown } from 'semantic-ui-react';

/**
 * CardMCFilter Modes:
 *
 * 'default'      → Start Year, Start Month  →  End Year, End Month   (4 inputs)
 * 'oneYearMonth' → Year, Start Month  →  End Month                   (1 year, 2 months)
 * 'yearOnly'     → Year                                               (1 input)
 */
export type FilterMode = 'default' | 'oneYearMonth' | 'oneYearOneMonth' | 'yearOnly';

type CardMeChartFilterProps = {
  mode?: FilterMode;
  year: number;
  month: number;
  endYear: number;
  endMonth: number;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
  onEndYearChange: (year: number) => void;
  onEndMonthChange: (month: number) => void;
  onSearch: () => void;
  onPrint: () => void;
  customFilter?: React.ReactNode;
  searchDisabled?: boolean;
  children?: React.ReactNode;
}

const CardMCFilter = (props: CardMeChartFilterProps) => {
  const {
    mode = 'default',
    year, month, endYear, endMonth,
    onYearChange, onMonthChange, onEndYearChange, onEndMonthChange,
    onSearch, onPrint, customFilter, searchDisabled, children,
  } = props;

  const [dateError, setDateError] = useState<string | null>(null);

  const handleSearch = () => {
    // 4-param validation: end year+month must not be before start year+month
    if (mode === 'default') {
      if (endYear < year || (endYear === year && endMonth < month)) {
        setDateError('End date must be later than or equal to start date');
        return;
      }
    }
    // 3-param validation: end month must be >= start month (same year)
    if (mode === 'oneYearMonth') {
      if (endMonth < month) {
        setDateError('End month must be later than or equal to start month');
        return;
      }
    }
    setDateError(null);
    onSearch();
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { key: y, value: y, text: y };
  });

  const monthOptions = [
    { key: 1, value: 1, text: 'มกราคม' },
    { key: 2, value: 2, text: 'กุมภาพันธ์' },
    { key: 3, value: 3, text: 'มีนาคม' },
    { key: 4, value: 4, text: 'เมษายน' },
    { key: 5, value: 5, text: 'พฤษภาคม' },
    { key: 6, value: 6, text: 'มิถุนายน' },
    { key: 7, value: 7, text: 'กรกฎาคม' },
    { key: 8, value: 8, text: 'สิงหาคม' },
    { key: 9, value: 9, text: 'กันยายน' },
    { key: 10, value: 10, text: 'ตุลาคม' },
    { key: 11, value: 11, text: 'พฤศจิกายน' },
    { key: 12, value: 12, text: 'ธันวาคม' },
  ];

  // Cropped options for end dropdowns
  // default mode: endYear must be >= startYear; endMonth must be >= startMonth when same year
  const endYearOptions = yearOptions.filter(opt => opt.value >= year);
  const endMonthOptionsDefault = endYear === year
    ? monthOptions.filter(opt => opt.value >= month)
    : monthOptions;
  // oneYearMonth mode: endMonth must be >= startMonth (same year)
  const endMonthOptionsOneYear = monthOptions.filter(opt => opt.value >= month);

  const label = (text: string, required = false) => (
    <span style={{ fontWeight: 'bold' }}>
      {text}{required && <span style={{ color: 'red' }}> *</span>}
    </span>
  );

  const sep = (text: string) => (
    <span style={{ padding: '0 4px', color: '#666' }}>{text}</span>
  );

  const renderInputs = () => {
    if (mode === 'yearOnly') {
      return (
        <>
          {label('Year', true)}
          <Dropdown
            selection compact
            options={yearOptions}
            value={year}
            onChange={(_, { value }) => onYearChange(value as number)}
            style={{ width: '100px' }}
          />
        </>
      );
    }

    if (mode === 'oneYearOneMonth') {
      return (
        <>
          {label('Year', true)}
          <Dropdown
            selection compact
            options={yearOptions}
            value={year}
            onChange={(_, { value }) => onYearChange(value as number)}
            style={{ width: '100px' }}
          />
          {label('Month', true)}
          <Dropdown
            selection compact
            options={monthOptions}
            value={month}
            onChange={(_, { value }) => onMonthChange(value as number)}
            style={{ width: '120px' }}
          />
        </>
      );
    }

    if (mode === 'oneYearMonth') {
      return (
        <>
          {label('Year', true)}
          <Dropdown
            selection compact
            options={yearOptions}
            value={year}
            onChange={(_, { value }) => { setDateError(null); onYearChange(value as number); }}
            style={{ width: '100px' }}
          />
          {label('Month', true)}
          <Dropdown
            selection compact
            options={monthOptions}
            value={month}
            onChange={(_, { value }) => {
              const newMonth = value as number;
              setDateError(null);
              onMonthChange(newMonth);
              // Snap endMonth forward if it falls behind the new startMonth
              if (endMonth < newMonth) onEndMonthChange(newMonth);
            }}
            style={{ width: '120px' }}
          />
          {sep('to')}
          <Dropdown
            selection compact
            options={endMonthOptionsOneYear}
            value={endMonth}
            onChange={(_, { value }) => { setDateError(null); onEndMonthChange(value as number); }}
            style={{ width: '120px' }}
          />
        </>
      );
    }

    // default — full Start Year+Month → End Year+Month
    return (
      <>
        {label('Start Year', true)}
        <Dropdown
          selection compact
          options={yearOptions}
          value={year}
          onChange={(_, { value }) => {
            const newYear = value as number;
            setDateError(null);
            onYearChange(newYear);
            // Snap endYear forward if it falls behind the new startYear
            if (endYear < newYear) {
              onEndYearChange(newYear);
              // If snapped to same year and endMonth is also behind, snap endMonth too
              if (endMonth < month) onEndMonthChange(month);
            }
          }}
          style={{ width: '100px' }}
        />
        {label('Start Month')}
        <Dropdown
          selection compact
          options={monthOptions}
          value={month}
          onChange={(_, { value }) => {
            const newMonth = value as number;
            setDateError(null);
            onMonthChange(newMonth);
            // Snap endMonth forward if same year and endMonth falls behind
            if (endYear === year && endMonth < newMonth) onEndMonthChange(newMonth);
          }}
          style={{ width: '120px' }}
        />
        {sep('to')}
        {label('End Year', true)}
        <Dropdown
          selection compact
          options={endYearOptions}
          value={endYear}
          onChange={(_, { value }) => {
            const newEndYear = value as number;
            setDateError(null);
            onEndYearChange(newEndYear);
            // If end year is now same as start year, snap endMonth if it's behind startMonth
            if (newEndYear === year && endMonth < month) onEndMonthChange(month);
          }}
          style={{ width: '100px' }}
        />
        {label('End Month')}
        <Dropdown
          selection compact
          options={endMonthOptionsDefault}
          value={endMonth}
          onChange={(_, { value }) => { setDateError(null); onEndMonthChange(value as number); }}
          style={{ width: '120px' }}
        />
      </>
    );
  };

  return (
    <div id="card-mc-filter">
      <div className="filter-inputs">
        {renderInputs()}
        {customFilter}
        <Button size='small' style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} onClick={handleSearch} disabled={searchDisabled}>Search</Button>
        <Button size='small' color='violet' onClick={onPrint} style={{ backgroundColor: '#EE82EE', color: 'white' }}>Print</Button>
        {children}
      </div>
      {dateError && (
        <div style={{ color: 'red', fontSize: '0.85em', marginTop: '4px', paddingLeft: '4px' }}>
          {dateError}
        </div>
      )}
    </div>
  );
};

export default CardMCFilter;
